import { safeStorage, createToggleRow } from '@learnforge/demo-kit';
import type { CustomExperimentSpec, RenderHandle } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import { createActionBar } from '../action-bar';
import { equivalentRatios, simplifyRatio } from '../arith/ratio';
import { createSvgEl } from '@learnforge/demo-render';
import { RATIO_PRESETS } from '../data/ratio-presets';
import type { ArithContext, ArithKey } from '../types';

const TANK_A_COLOR = 'hsl(205, 70%, 55%)';
const TANK_B_COLOR = 'hsl(25, 70%, 55%)';

// Interpolates the blend colour between tank A's hue (205, blue) and tank B's
// hue (25, orange) by the B-fraction of the mix. Pure → unit-testable.
export function tankColour(tankA: number, tankB: number): string {
  const total = tankA + tankB;
  const t = total === 0 ? 0.5 : tankB / total;
  const hueA = 205;
  const hueB = 25;
  const hue = hueA + (hueB - hueA) * t;
  return `hsl(${Math.round(hue)}, 70%, 55%)`;
}

const TANK_TOP = 40;
const TANK_H = 220;
const TANK_W = 70;

interface State {
  tankA: number;
  tankB: number;
  advanced: boolean;
}

const ADVANCED_KEY = 'arithmetic.ratio-mixer.advanced';

function buildSpec(): CustomExperimentSpec<State, ArithKey, ArithContext> {
  return {
    labelKey: 'experiment.ratio-mixer.label' as ArithKey,
    demoId: 'arithmetic',
    advancedKey: 'ratio-mixer',

    initialState(ctx): State {
      return {
        tankA: 2,
        tankB: 3,
        advanced: ctx.advanced,
      };
    },

    applyPreset(state, presetId): State {
      const preset = RATIO_PRESETS.find((p) => p.id === presetId);
      if (!preset) return state;
      return { ...state, tankA: preset.a, tankB: preset.b };
    },

    createRender(ctx, host, controls): RenderHandle<State> {
      // ── Stage SVG ──
      const svg = createSvgEl('svg', { viewBox: '0 0 640 340', class: 'arith-stage' });
      host.appendChild(svg);
      const scene = createSvgEl('g', {});
      svg.appendChild(scene);

      // Transient DOM refs (not part of State)
      let aInput: HTMLInputElement | null = null;
      let bInput: HTMLInputElement | null = null;

      // A tank whose liquid level is proportional to its amount (relative to the
      // larger of the two tanks), so changing the ratio visibly changes the fill.
      function drawTank(
        x: number,
        amount: number,
        maxUnit: number,
        colour: string,
        name: string,
      ): void {
        const fillH = (amount / maxUnit) * TANK_H;
        const outline = createSvgEl('rect', {
          class: 'arith-tank',
          x,
          y: TANK_TOP,
          width: TANK_W,
          height: TANK_H,
          rx: 6,
        });
        const liquid = createSvgEl('rect', {
          x,
          y: TANK_TOP + TANK_H - fillH,
          width: TANK_W,
          height: fillH,
          rx: 6,
          fill: colour,
        });
        const label = createSvgEl('text', {
          class: 'arith-readout',
          x: x + TANK_W / 2,
          y: TANK_TOP + TANK_H + 26,
          'text-anchor': 'middle',
        });
        label.textContent = `${name}: ${amount}`;
        scene.appendChild(liquid);
        scene.appendChild(outline);
        scene.appendChild(label);
      }

      function rerender(state: State): void {
        while (scene.firstChild) scene.removeChild(scene.firstChild);
        const { tankA, tankB } = state;
        const maxUnit = Math.max(1, tankA, tankB);

        drawTank(40, tankA, maxUnit, TANK_A_COLOR, 'A');
        drawTank(140, tankB, maxUnit, TANK_B_COLOR, 'B');

        // Beaker: the two amounts stacked as proportional bands — the ratio made visible.
        const beakerX = 300;
        const beakerW = 120;
        const total = tankA + tankB;
        if (total > 0) {
          const aH = (tankA / total) * TANK_H;
          const bH = (tankB / total) * TANK_H;
          const aBand = createSvgEl('rect', {
            x: beakerX,
            y: TANK_TOP + TANK_H - aH,
            width: beakerW,
            height: aH,
            fill: TANK_A_COLOR,
          });
          const bBand = createSvgEl('rect', {
            x: beakerX,
            y: TANK_TOP + TANK_H - aH - bH,
            width: beakerW,
            height: bH,
            fill: TANK_B_COLOR,
          });
          scene.appendChild(aBand);
          scene.appendChild(bBand);
        }
        const beaker = createSvgEl('rect', {
          class: 'arith-beaker',
          x: beakerX,
          y: TANK_TOP,
          width: beakerW,
          height: TANK_H,
          rx: 6,
        });
        scene.appendChild(beaker);

        const simplified = simplifyRatio([tankA, tankB]);
        const sa = simplified[0] ?? 0;
        const sb = simplified[1] ?? 0;
        const ratioLabel = createSvgEl('text', {
          class: 'arith-readout',
          x: beakerX + beakerW / 2,
          y: TANK_TOP + TANK_H + 26,
          'text-anchor': 'middle',
        });
        ratioLabel.textContent = `${sa} : ${sb}`;
        scene.appendChild(ratioLabel);

        // Equivalent-ratios strip.
        const eqs = equivalentRatios([tankA, tankB], 4);
        eqs.forEach((r, i) => {
          const txt = createSvgEl('text', { class: 'arith-ratio-eq', x: 470, y: 70 + i * 34 });
          txt.textContent = r.join(' : ');
          scene.appendChild(txt);
        });
      }

      // ── Action bar — built ONCE with static preset list ──
      const actionBar = createActionBar(
        ctx.actionBar,
        RATIO_PRESETS.map((p) => ({
          id: p.id,
          labelKey: `${p.a}:${p.b}`,
          active: p.a === 2 && p.b === 3, // default state matches two-to-three
          onClick() {
            controls.setState((s) => ({
              ...s,
              tankA: p.a,
              tankB: p.b,
            }));
          },
        })),
      );

      // ── Panel — two advanced-only number inputs + advanced toggle ──
      function makeTankInput(which: 'tankA' | 'tankB', name: string): HTMLElement {
        const wrap = document.createElement('div');
        wrap.className = 'arith-frac-input';
        const label = document.createElement('label');
        label.textContent = name;
        const input = document.createElement('input');
        input.type = 'number';
        input.min = '0';
        input.max = '1000';
        input.step = '1';
        input.addEventListener('change', () => {
          const v = Math.floor(Number(input.value));
          if (Number.isFinite(v) && v >= 0 && v <= 1000) {
            controls.setState((s) => ({ ...s, [which]: v }));
          }
        });
        wrap.appendChild(label);
        wrap.appendChild(input);
        if (which === 'tankA') aInput = input;
        else bInput = input;
        return wrap;
      }

      // Render the panel once: advanced inputs + toggle
      const advToggle = createToggleRow({
        i18n: ctx.i18n,
        labelKey: 'btn.advanced' as ArithKey,
        checked: ctx.advanced,
        onChange(next) {
          safeStorage.set(ADVANCED_KEY, next ? 'true' : 'false');
          controls.setState((s) => ({ ...s, advanced: next }));
        },
      });

      // Build panel content (once)
      function buildPanel(advanced: boolean): void {
        aInput = null;
        bInput = null;
        ctx.panel.replaceChildren();
        if (advanced) {
          ctx.panel.appendChild(makeTankInput('tankA', 'A'));
          ctx.panel.appendChild(makeTankInput('tankB', 'B'));
        }
        ctx.panel.appendChild(advToggle.element);
      }

      buildPanel(ctx.advanced);

      // ── RenderHandle ──
      let lastAdvanced: boolean = ctx.advanced;

      const update = (state: State): void => {
        // Rebuild panel only when advanced flag changes
        if (state.advanced !== lastAdvanced) {
          lastAdvanced = state.advanced;
          buildPanel(state.advanced);
        }

        // Reconcile SVG scene
        rerender(state);

        // Guard number-input value sync
        if (aInput !== null && aInput.value !== String(state.tankA)) {
          aInput.value = String(state.tankA);
        }
        if (bInput !== null && bInput.value !== String(state.tankB)) {
          bInput.value = String(state.tankB);
        }

        // Update action bar active state
        actionBar.setButtons(
          RATIO_PRESETS.map((p) => ({
            id: p.id,
            labelKey: `${p.a}:${p.b}`,
            active: p.a === state.tankA && p.b === state.tankB,
            onClick() {
              controls.setState((s) => ({
                ...s,
                tankA: p.a,
                tankB: p.b,
              }));
            },
          })),
        );
      };

      return {
        update,
        dispose() {
          actionBar.destroy();
        },
      };
    },

    applyShotState(state): State {
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());
